"use client";

import { AnimatePresence, motion } from "framer-motion";
import { Check, Loader2, type LucideIcon, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { EASE } from "../../constants";

export type NodeState = "idle" | "running" | "done";

// ease-in-out for the workflow trace (matches Attio's measured line/outline draws)
const EASE_TRACE = [0.65, 0, 0.35, 1] as const;
const TRACE_COLOR = "hsl(var(--brand-accent))";

export function useElementSize<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setSize({ w: el.offsetWidth, h: el.offsetHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, size] as const;
}

function StatusBadge({
  state,
  doneLabel = "Completed",
  doneNegative = false,
  labelClassName,
  reduce,
}: {
  state: NodeState;
  doneLabel?: string;
  doneNegative?: boolean;
  labelClassName?: string;
  reduce: boolean;
}) {
  return (
    <span className="ml-auto flex shrink-0 items-center">
      <AnimatePresence mode="popLayout" initial={false}>
        {state !== "idle" ? (
          <motion.span
            key={state}
            initial={reduce ? false : { opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={reduce ? undefined : { opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.2, ease: EASE }}
            className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-border bg-background px-2 py-0.5 text-[11px] font-medium text-foreground"
          >
            {state === "running" ? (
              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
            ) : doneNegative ? (
              <X className="h-3 w-3 text-red-500" />
            ) : (
              <Check className="h-3 w-3 text-success" />
            )}
            <span className={labelClassName}>{state === "running" ? "Running" : doneLabel}</span>
          </motion.span>
        ) : null}
      </AnimatePresence>
    </span>
  );
}

// Accent outline that traces around the active node (Attio-style). The draw
// starts at the top-center and runs down both sides simultaneously, meeting at
// the bottom-center — so the trace originates from the middle, not a corner.
function NodeOutline({
  active,
  reduce,
  w,
  h,
}: {
  active: boolean;
  reduce: boolean;
  w: number;
  h: number;
}) {
  if (!active || w === 0 || h === 0) return null;

  const r = 11.5;
  const left = 0.5;
  const right = w - 0.5;
  const top = 0.5;
  const bottom = h - 0.5;
  const cx = w / 2;

  // Top-center → clockwise down the right side → bottom-center.
  const rightHalf = `M ${cx},${top} H ${right - r} A ${r},${r} 0 0 1 ${right},${top + r} V ${bottom - r} A ${r},${r} 0 0 1 ${right - r},${bottom} H ${cx}`;
  // Top-center → counter-clockwise down the left side → bottom-center.
  const leftHalf = `M ${cx},${top} H ${left + r} A ${r},${r} 0 0 0 ${left},${top + r} V ${bottom - r} A ${r},${r} 0 0 0 ${left + r},${bottom} H ${cx}`;

  const draw = {
    initial: reduce ? false : { pathLength: 0 },
    animate: { pathLength: 1 },
    transition: { duration: reduce ? 0 : 1.1, ease: EASE_TRACE },
  } as const;

  return (
    <svg
      className="pointer-events-none absolute inset-0 z-10 overflow-visible"
      width={w}
      height={h}
      fill="none"
      aria-hidden
    >
      <motion.path
        d={rightHalf}
        stroke={TRACE_COLOR}
        strokeWidth={1}
        strokeLinecap="round"
        {...draw}
      />
      <motion.path
        d={leftHalf}
        stroke={TRACE_COLOR}
        strokeWidth={1}
        strokeLinecap="round"
        {...draw}
      />
    </svg>
  );
}

export function NodeShell({
  state,
  className,
  reduce,
  children,
}: {
  state: NodeState;
  className?: string;
  reduce: boolean;
  children: React.ReactNode;
}) {
  const [ref, { w, h }] = useElementSize<HTMLDivElement>();
  const active = state !== "idle";
  return (
    <div
      ref={ref}
      className={cn(
        "relative rounded-xl border border-border bg-background shadow-sm transition-all duration-500",
        active ? "opacity-100 shadow-[0_0_0_4px_hsl(var(--brand-accent)/0.08)]" : "opacity-45",
        className,
      )}
    >
      <NodeOutline active={active} reduce={reduce} w={w} h={h} />
      <div className="overflow-hidden rounded-xl">{children}</div>
    </div>
  );
}

export function NodeHead({
  icon: Icon,
  kind,
  title,
  iconClass,
  state,
  doneLabel,
  doneNegative,
  statusLabelClassName,
  reduce,
}: {
  icon: LucideIcon;
  kind: string;
  title: string;
  iconClass?: string;
  state?: NodeState;
  doneLabel?: string;
  doneNegative?: boolean;
  statusLabelClassName?: string;
  reduce?: boolean;
}) {
  return (
    <div className="flex items-center gap-2.5 px-3.5 py-2.5">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border bg-background">
        <Icon className={cn("h-3.5 w-3.5 text-muted-foreground", iconClass)} />
      </span>
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {kind}
        </p>
        <p className="truncate text-sm font-semibold text-foreground">{title}</p>
      </div>
      {state ? (
        <StatusBadge
          state={state}
          doneLabel={doneLabel}
          doneNegative={doneNegative}
          labelClassName={statusLabelClassName}
          reduce={reduce ?? false}
        />
      ) : null}
    </div>
  );
}

// Vertical line that "draws" the accent downward over a gray track when active.
export function Connector({
  active,
  reduce,
  delay = 0,
  className,
}: {
  active: boolean;
  reduce: boolean;
  delay?: number;
  className?: string;
}) {
  return (
    <div className={cn("relative mx-auto w-px overflow-hidden rounded-full bg-border", className)}>
      <motion.div
        className="absolute inset-x-0 top-0 rounded-full bg-[hsl(var(--brand-accent))]"
        initial={reduce ? false : { height: "0%" }}
        animate={{ height: active ? "100%" : "0%" }}
        transition={{
          duration: active && !reduce ? 0.6 : 0,
          ease: EASE_TRACE,
          delay: active ? delay : 0,
        }}
      />
    </div>
  );
}

// Horizontal half-segment used by the desktop branch fork; only the chosen
// side is traced. `side` controls which direction the line draws from center.
export function ForkBranch({
  side,
  active,
  reduce,
  delay = 0.45,
}: {
  side: "left" | "right";
  active: boolean;
  reduce: boolean;
  delay?: number;
}) {
  return (
    <motion.div
      className={cn(
        "absolute top-0 h-px rounded-full bg-[hsl(var(--brand-accent))]",
        side === "left" ? "right-1/2" : "left-1/2",
      )}
      initial={reduce ? false : { width: "0%" }}
      animate={{ width: active ? "25%" : "0%" }}
      transition={{
        duration: active && !reduce ? 0.45 : 0,
        ease: EASE_TRACE,
        delay: active ? delay : 0,
      }}
    />
  );
}

export function FirstNamePill({
  resolved,
  name,
  tone,
}: {
  resolved: boolean;
  name: string;
  tone: "light" | "violet";
}) {
  // When resolved, render plain inline bold text so it sits on the same
  // baseline as the surrounding copy (no pill chrome to throw off alignment).
  if (resolved) {
    return (
      <motion.span
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.25, ease: EASE }}
        className={cn("font-semibold", tone === "light" ? "text-white" : "text-foreground")}
      >
        {name}
      </motion.span>
    );
  }
  return (
    <span
      className={cn(
        "mx-0.5 inline-flex items-center gap-1 whitespace-nowrap rounded-md px-1.5 py-0.5 align-middle text-[11px] font-semibold leading-none",
        tone === "light" ? "bg-white/20 text-white" : "bg-primary/10 text-primary",
      )}
    >
      <span className="opacity-60">{"{ }"}</span> First name
    </span>
  );
}

export function BranchBadge({ tone, active }: { tone: "yes" | "no"; active: boolean }) {
  return (
    <span
      className={cn(
        "rounded-full border px-2.5 py-0.5 text-[10px] font-bold tracking-wide transition-opacity duration-300",
        tone === "yes"
          ? "border-green-500/30 bg-green-500/10 text-green-600"
          : "border-red-500/30 bg-red-500/10 text-red-600",
        active ? "opacity-100" : "opacity-50",
      )}
    >
      {tone === "yes" ? "YES" : "NO"}
      <span className="hidden sm:inline">{tone === "yes" ? " · Opted in" : " · Not opted in"}</span>
    </span>
  );
}
